import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, CircularProgress, Divider, Grid, Stack } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import DataApi from 'api/faber/DataApi'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import ColorBox from 'common/Box/ColorWhiteBox'
import ProfileListInfo from 'common/FaberComponents/Layout/FaberColumnLayout'
import NormalText from 'common/Typography/NormalText'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'
import { mapStatesToFrontendDropdown } from 'helpers/backendStatesMapping'
import OrbitSession from 'hooks/useCookies'
import { TransformedFaberUserData } from 'types/api/faber/globalCustomers.type'
import {
  GetActiveUserPlansDetailsType,
  GetAllFaberUsersDetails,
  GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
} from 'types/api/faber/oneTimePack.type'

import ActionMenuForOneUser from '../v2-customer-components/ActionMenuForOneUser'
import EsimRequestDialog from '../v2-customer-components/EsimRequestV2Dialog'
import PromocodesDisplayForOneUser from '../v2-customer-components/PromocodesDisplayForOneUser'
import RefreshNetworkDialog from '../v2-customer-components/RefreshNetworkDialog'

import CustomerPlansCard from './CustomerPlansCard'
import DataRemainingCardV2 from './DataRemainingCardV2'
import EsimStatusCardV2 from './EsimStatusCardV2'
import NetworkCard from './NetworkCard'
import SubHistoryV2 from './SubHistoryV2'
import TerminatedCustomerPlans from './TerminatedCustomerPlans'
import UpdateUserDetailsDialog from './UpdateUserDetailsDialog'

interface UserProfileProps {
  customerRow: TransformedFaberUserData
}

export default function UserProfileV3({ customerRow }: UserProfileProps) {
  const faberToken = OrbitSession.get('faberToken')

  const location = useLocation()

  // all dialogs
  const [isRefreshNetworkDialogOpen, setIsRefreshNetworkDialogOpen] =
    useState(false)

  const [isEsimEmailDialogOpen, setIsEsimEmailDialogOpen] = useState(false)
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false)

  //CX USER PLAN
  const [allInactiveUserPlans, setAllInactiveUserPlans] = useState<
    GetAllInactiveAndTerminatedOtpackUserPlansDetailsType[]
  >([])

  const [activeOtpackUserPlans, setActiveOtpackUserPlans] = useState<
    GetActiveUserPlansDetailsType[]
  >([])

  const [iccid, setIccid] = useState('')

  //DATA REAMINING CARD
  const [dataRemaining, setDataRemaining] = useState(0)
  const [otpackDataRemaining, setOtpackDataRemaining] = useState(0)

  //ESIM
  const [esimStatus, setEsimStatus] = useState('')
  const [tooltipInfo, setTooltipInfo] = useState('')
  const [esimTextColor, setEsimTextColor] = useState('')

  //GET ALL ACTIVE OTPACK USER PLANS API
  const { refetch: refetchActiveOtpackUserPlans } = useQuery(
    ['getAllActiveOtpackUserPlans', customerRow.userID],
    async () => {
      return OneTimePackApi.getActiveOtpUserPacks(
        customerRow.userID,
        faberToken
      )
    },
    {
      onSuccess: (res) => {
        setActiveOtpackUserPlans(res.data.body.data)

        return res.data
      },
      onError: () => {
        return []
      },
      enabled: customerRow.otpack
    }
  )

  //GET ALL INACTIVE OTPACK USER PLANS API
  const { refetch: refetchAllInactiveAndTerminatedUserPlans } = useQuery(
    ['getAllInactiveAndTerminatedOtpackUserPlans', customerRow.userID],
    async () => {
      return OneTimePackApi.getAllInactiveOtpackUserPlans(
        customerRow.userID,
        faberToken
      )
    },
    {
      onSuccess: (res) => {
        setAllInactiveUserPlans(res.data.body.data)
      },
      onError: () => {
        return []
      },
      enabled: customerRow.otpack
    }
  )

  //eSIM STATUS API (gets ICCID)
  const { isLoading: isEsimStatusLoading, refetch: refetchEsimStatus } =
    useQuery(
      ['get-esim-status', customerRow.userID],
      async () => {
        const { data } = await DataApi.getEsimStatus(
          faberToken,
          customerRow.userID
        )
        return data
      },
      {
        onSuccess: (res) => {
          setIccid(res.data.iccid)
          setEsimStatus(res.data.state)
          switch (res.data.state) {
            case 'RELEASED':
              setTooltipInfo(
                'The eSIM has been sent to the user but not downloaded into a device yet'
              )
              setEsimTextColor(circlesOrbitColorPalette.neutrals[500])

              break
            case 'DISABLED':
              setTooltipInfo(
                'The eSIM has been downloaded into a device but not turned on. Advise user to turn on the line from their phone settings'
              )
              setEsimTextColor('#ed6c02')
              break
            case 'ENABLED':
              setTooltipInfo(
                'The eSIM has been downloaded into a device and is active'
              )
              setEsimTextColor(circlesOrbitColorPalette.success.main)
              break
            case 'DELETED':
              setTooltipInfo(
                'The eSIM profile has been removed from the device'
              )
              setEsimTextColor(circlesOrbitColorPalette.error.main)
              break
            case 'ERROR':
              setTooltipInfo('Please perform an eSim switch the customer')
              setEsimTextColor(circlesOrbitColorPalette.error.main)
              break
          }
          return res.data
        },
        enabled: !!customerRow.userID
      }
    )

  //DATA REMAINING CARD API FOR SUBSCRIPTION USERS
  const { data: subdata, isLoading: isSubDataLoading } = useQuery(
    ['getOverviewCRDData', iccid],
    async () => {
      const getCDROverviewDataResponse = await DataApi.getDataCDROverview(
        iccid,
        faberToken
      )
      return getCDROverviewDataResponse.data
    },
    {
      onSuccess: (res) => {
        return res
      },
      onError: () => {
        return {}
      },
      //TODO: CHECK IF NEED && SUBSCRIPTION
      enabled: !!iccid && customerRow.subscription
    }
  )

  useEffect(() => {
    if (subdata && customerRow.subscription) {
      setDataRemaining(subdata.totalDataAllocatedMB - subdata.totalDataUsedMB)
    } else {
      setDataRemaining(0)
    }
  }, [subdata])

  /* FUNCTION CALLS */
  const allFields = useMemo(() => {
    return [
      {
        mainFieldTitle: 'Profile',
        fields: [
          {
            fieldName: 'First Name',
            fieldValue: customerRow.firstName
          },
          {
            fieldName: 'Last Name',
            fieldValue: customerRow.lastName
          },
          {
            fieldName: 'Email',
            fieldValue: customerRow.userEmail
          }
        ]
      }
    ]
  }, [customerRow])

  // EVENT HANDLER FUNCTIONS

  const handleRefreshNetworkDialogOpen = () => {
    setIsRefreshNetworkDialogOpen(true)
  }
  const handleRefreshNetworkDialogClose = () => {
    setIsRefreshNetworkDialogOpen(false)
  }

  const handleEsimDialogOpen = () => {
    setIsEsimEmailDialogOpen(true)
  }

  const handleEsimDialogClose = () => {
    setIsEsimEmailDialogOpen(false)
  }

  // const handleUserDialogOpen = () => {
  //   setIsEditUserDialogOpen(true)
  // }

  const handleUserDialogClose = () => {
    setIsEditUserDialogOpen(false)
  }

  useEffect(() => {
    refetchActiveOtpackUserPlans()
    refetchAllInactiveAndTerminatedUserPlans()
    refetchEsimStatus()
  }, [location, customerRow])

  const updateUserDetails = () => {
    refetchEsimStatus()
  }

  const transformedRowInformation = useMemo(() => {
    const newCustomerRow: TransformedFaberAndGlobalUsersType = {
      ...customerRow,
      iccid: iccid
    }
    return newCustomerRow
  }, [customerRow, iccid])

  return (
    <>
      {/* ALL DIALOGS FOR ACTIONS HERE */}
      <UpdateUserDetailsDialog
        faberToken={faberToken}
        handleUserDialogClose={handleUserDialogClose}
        rowInfo={transformedRowInformation}
        dialogOpen={isEditUserDialogOpen}
      />

      <RefreshNetworkDialog
        faberToken={faberToken}
        handleRefreshNetworkDialogClose={handleRefreshNetworkDialogClose}
        refreshNetworkDialogOpen={isRefreshNetworkDialogOpen}
        customerRow={transformedRowInformation}
      />

      <EsimRequestDialog
        faberToken={faberToken}
        handleRequestEsimDialogClose={handleEsimDialogClose}
        requestEsimDialog={isEsimEmailDialogOpen}
        customerRow={transformedRowInformation}
        productType='otp'
        updateUserDetails={updateUserDetails}
      />

      <Stack spacing={3} mt={3}>
        <Stack>
          <NormalText isBold variant='h3'>
            Check the following steps to troubleshoot network issues:
          </NormalText>
          <NormalText>
            {' '}
            <em>
              Please get customers to take screenshots at each stage and send it
              as the screenshots will be needed if issue needs to be escalated
            </em>
          </NormalText>
        </Stack>
        <Grid container spacing={4}>
          <Grid item>
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '34px',
                p: '24px',
                maxWidth: '215px',
                maxHeight: '280px',
                position: 'relative'
              }}
            >
              {isSubDataLoading && customerRow ? (
                <CircularProgress />
              ) : (
                <DataRemainingCardV2
                  dataRemaining={dataRemaining}
                  customerRow={customerRow}
                  otpackDataRemaining={otpackDataRemaining}
                />
              )}
            </Box>
          </Grid>

          <Grid item>
            {isEsimStatusLoading ? (
              <CircularProgress />
            ) : (
              <EsimStatusCardV2
                esimStatus={esimStatus}
                tooltipInfo={tooltipInfo}
                esimTextColor={esimTextColor}
              />
            )}
          </Grid>
          <Grid item>
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '34px',
                p: '24px',
                maxWidth: '215px',
                maxHeight: '280px',
                position: 'relative'
              }}
            >
              {isSubDataLoading && customerRow ? (
                <CircularProgress />
              ) : (
                <NetworkCard />
              )}
            </Box>
          </Grid>
        </Grid>

        <Grid item>
          <ColorBox>
            <Stack
              flexDirection={'row'}
              sx={{ justifyContent: 'flex-end', mb: 2 }}
            >
              <ActionMenuForOneUser
                productType='otp'
                handleEsimRequestDialogOpen={() => void 0}
                handleRefreshNetworkDialogOpen={handleRefreshNetworkDialogOpen}
                handleWinbackDialogOpen={() => void 0}
                handleSuspendOrUnsuspendDialogOpen={() => void 0}
                showRefreshNetworkButton
                handleEsimRequestDialogV2Open={handleEsimDialogOpen}
                handleCancelSubDialogOpen={() => void 0}
              />
            </Stack>
            <Box sx={{ p: 2 }}>
              {allFields.map((field, index) => (
                <React.Fragment key={index + field.mainFieldTitle}>
                  {field.fields && field.fields.length > 0 && (
                    <ProfileListInfo
                      title={field.mainFieldTitle}
                      fields={field.fields.map((item) => ({
                        fieldName: item.fieldName,
                        fieldValue: item.fieldValue || ''
                      }))}
                    >
                      {/* TO DO: WHEN EDIT USER IS UP FOR ONE TIME PACK */}
                      {/* <EditIconImg onEdit={handleUserDialogOpen} /> */}
                    </ProfileListInfo>
                  )}
                  {index === 0 && (
                    <>
                      <ProfileListInfo
                        proportion={4}
                        title=''
                        fields={[
                          {
                            fieldName: 'MSISDN',
                            fieldValue: customerRow.msisdn
                          },
                          {
                            fieldName: 'ICCID',
                            fieldValue: iccid
                          },

                          {
                            fieldName: 'Customer Id',
                            fieldValue: customerRow.userID
                          }
                        ]}
                      />
                    </>
                  )}
                  {index === 1 && (
                    <ProfileListInfo
                      proportion={4}
                      title=''
                      fields={[
                        {
                          fieldName: 'Account Status',
                          fieldValue: mapStatesToFrontendDropdown(
                            customerRow.planStatus ? customerRow.planStatus : ''
                          )
                        }
                      ]}
                    />
                  )}
                  {index !== allFields.length - 1 && (
                    <Divider sx={{ width: '100%' }} />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </ColorBox>
        </Grid>
        <ColorBox>
          {customerRow.subscription ? (
            <SubHistoryV2 />
          ) : (
            <CustomerPlansCard
              setOtpackDataRemaining={setOtpackDataRemaining}
              activeOtpackUserPlans={activeOtpackUserPlans}
              allInactiveUserPlans={allInactiveUserPlans}
              userId={customerRow.userID}
            />
          )}
        </ColorBox>
        <ColorBox>
          <TerminatedCustomerPlans />
        </ColorBox>
        <ColorBox>
          <>
            <NormalText isBold variant='h5'>
              Promocodes/Vouchers
            </NormalText>
            <PromocodesDisplayForOneUser userId={customerRow.userID} />
          </>
        </ColorBox>
      </Stack>
    </>
  )
}

export interface TransformedFaberUsersType extends GetAllFaberUsersDetails {
  iccid: string
}

export interface TransformedFaberAndGlobalUsersType
  extends TransformedFaberUserData {
  iccid: string
}
